import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle,
  FileText,
  CreditCard,
  Users,
  Calendar,
  ArrowRight,
  Wallet
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import RecordPaymentDialog from './RecordPaymentDialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

function TreasurerDashboard({ user }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_revenue: 0,
    pending_amount: 0,
    overdue_amount: 0,
    collection_rate: 0,
    total_invoices: 0,
    paid_invoices: 0,
    pending_invoices: 0,
    overdue_invoices: 0
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);

  useEffect(() => {
    fetchTreasurerData();
  }, []);

  const fetchTreasurerData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch financial statistics
      const statsResponse = await axios.get(`${API}/api/invoices/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch recent invoices
      const invoicesResponse = await axios.get(`${API}/api/invoices?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch recent payments
      const paymentsResponse = await axios.get(`${API}/api/payments?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(statsResponse.data);
      setRecentInvoices(invoicesResponse.data);
      setRecentPayments(paymentsResponse.data);
      
    } catch (error) {
      console.error('Error fetching treasurer data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données financières',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PAID':
        return 'Payée';
      case 'PENDING':
        return 'En attente';
      case 'OVERDUE':
        return 'En retard';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données financières...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bonjour, {user.first_name} !</h1>
          <p className="text-gray-600 mt-1">Tableau de bord Trésorier</p>
        </div>
        <div className="flex items-center space-x-2">
          <Wallet className="w-8 h-8 text-green-600" />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Revenue */}
        <Card className="card hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenus Totaux</CardTitle>
            <DollarSign className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.total_revenue || 0)}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {stats.paid_invoices || 0} factures payées
            </p>
          </CardContent>
        </Card>

        {/* Pending Amount */}
        <Card className="card hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En Attente</CardTitle>
            <TrendingUp className="h-5 w-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {formatCurrency(stats.pending_amount || 0)}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {stats.pending_invoices || 0} factures en attente
            </p>
          </CardContent>
        </Card>

        {/* Overdue Amount */}
        <Card className="card hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En Retard</CardTitle>
            <AlertCircle className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(stats.overdue_amount || 0)}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {stats.overdue_invoices || 0} factures en retard
            </p>
          </CardContent>
        </Card>

        {/* Collection Rate */}
        <Card className="card hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de Recouvrement</CardTitle>
            <CheckCircle className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {stats.collection_rate || 0}%
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {stats.total_invoices || 0} factures totales
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="card">
        <CardHeader>
          <CardTitle>Actions Rapides</CardTitle>
          <CardDescription>Accès rapide aux fonctionnalités principales</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button 
              className="h-20 flex flex-col items-center justify-center space-y-2"
              onClick={() => navigate('/dashboard/billing')}
            >
              <FileText className="w-6 h-6" />
              <span>Facturation</span>
            </Button>
            
            <RecordPaymentDialog 
              trigger={
                <Button 
                  className="h-20 flex flex-col items-center justify-center space-y-2"
                  variant="outline"
                >
                  <CreditCard className="w-6 h-6" />
                  <span>Enregistrer Paiement</span>
                </Button>
              }
              onSuccess={fetchTreasurerData}
            />
            
            <Button 
              className="h-20 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/cantine-transport')}
            >
              <Users className="w-6 h-6" />
              <span>Cantine & Transport</span>
            </Button>
            
            <Button 
              className="h-20 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/billing')}
            >
              <Calendar className="w-6 h-6" />
              <span>Relances</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Invoices and Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <Card className="card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Factures Récentes</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard/billing')}
              >
                Voir tout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentInvoices.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucune facture récente
                </p>
              ) : (
                recentInvoices.map((invoice) => (
                  <div 
                    key={invoice.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/dashboard/billing')}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {invoice.student_name || 'Élève'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {invoice.description || 'Facture scolaire'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Échéance: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-bold text-sm">
                        {formatCurrency(invoice.amount || invoice.total_amount || 0)}
                      </p>
                      <Badge className={`text-xs ${getStatusColor(invoice.status)}`}>
                        {getStatusLabel(invoice.status)}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Paiements Récents</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard/billing')}
              >
                Voir tout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentPayments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun paiement récent
                </p>
              ) : (
                recentPayments.map((payment) => (
                  <div 
                    key={payment.id} 
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-sm">
                          {payment.student_name || 'Élève'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {payment.payment_method || 'Espèces'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm text-green-600">
                        {formatCurrency(payment.amount || 0)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Tips */}
      <Card className="card bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">💡 Conseil Trésorier</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-800">
            {stats.overdue_invoices > 0 
              ? `Vous avez ${stats.overdue_invoices} facture(s) en retard. Pensez à envoyer des relances aux parents concernés.`
              : 'Excellent ! Toutes les factures sont à jour. Continuez ce bon travail !'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default TreasurerDashboard;
